import {
  addDays, getMinutes, differenceInDays, differenceInMinutes, format, isBefore, isToday as isTheGivenDateToday, isValid, getHours, eachYearOfInterval, subYears,
} from 'date-fns';
import { id } from 'date-fns/locale';

/**
 * Date format according to the given pattern
 *
 * @param {string, date} date - raw date to format
 * @param {string} pattern - pattern use to format the date
 * Check out {@link https://date-fns.org/v2.28.0/docs/format | date-fns official docs}
 * for more information
 * @returns {string}
 */
export const formatDate = (date, pattern = 'dd/MM/yyyy') => {
  const rawDate = new Date(date);

  if (isValid(rawDate)) {
    return format(rawDate, pattern, { locale: id });
  }

  return date;
};

/**
 * Is the given date today
 *
 * @param {Date} date - date to check
 * Check out {@link https://date-fns.org/v2.28.0/docs/isToday | date-fns official docs}
 * for more information
 * @returns {boolean}
 */
export const isToday = (date) => isTheGivenDateToday(new Date(date));

/**
 * Get the number of full day periods between two dates.
 *
 * @param {Date} laterDate - the later date
 * @param {Date} earlierDate - the earlier date
 * @returns {Number}
 */
export function daysDifference(laterDate, earlierDate) {
  return differenceInDays(new Date(laterDate), new Date(earlierDate));
}

/**
 * Get the signed number of full (rounded towards 0) minutes between the given dates.
 *
 * @param {Date} laterDate - the later date
 * @param {Date} earlierDate - the earlier date
 * @returns {Number}
 */
export function minutesDifference(laterDate, earlierDate) {
  return differenceInMinutes(new Date(laterDate), new Date(earlierDate));
}

/**
 * Add the specified number of days to the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of days to be added.
 * @returns {Date} - the new date with the days added
 */
export const addDay = (date, amount) => addDays(new Date(date), amount);

/**
 * Get the hours of the given date.
 *
 * @param {Date} date - the given date
 * @returns {Number} - the hours
 */
export const getHour = (date) => getHours(new Date(date));

/**
 * Get the minutes of the given date.
 *
 * @param {Date} date - the given date
 * @returns {Number} - the minutes
 */
export const getMinute = (date) => getMinutes(new Date(date));

/**
 * Get list of 5 last years.
 *
 * @returns {Array} - list of 5 last years
 */
export function getListOfLast5Years() {
  // Get the current year
  const currentYear = new Date().getFullYear();

  // Define the range of past 5 years
  const endDate = new Date(currentYear + 1, 0, 1); // January 1st of the next year
  const startDate = subYears(endDate, 4); // Subtract 4 years from the end date to get 5 years back

  // Generate an array of years for the past 5 years
  const yearsArray = eachYearOfInterval({ start: startDate, end: endDate });

  // Convert each date object to its respective year
  return yearsArray.map((date) => date.getFullYear()).reverse();
}

/**
 * Is the given date before current date time
 *
 * @param {Date} date - the date to compare with current date time
 * @returns {Number}
 */
export function isBeforeCurrentDateTime(date) {
  return isBefore(new Date(date), new Date());
}
